import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

// TRADE SOUNDS (owner 2026-10-29: "when a trade is taken and exited, a sound should come so I know the status").
// Watches YOUR trade log (/api/my/trade-log, every 4 s): TRADE OPENED -> rising tone + "trade taken";
// TRADE CLOSED -> happy chime on a profit / low tone on a loss + the P&L spoken, a box on screen and a
// phone / desktop notification. The 🔊 switch turns it off (remembered on this device). Trading is unaffected.
let AC = null;
function audio() {
  try { if (!AC) { const C = window.AudioContext || window.webkitAudioContext; AC = new C(); } if (AC.state === 'suspended') AC.resume(); } catch (e) { AC = null; }
  return AC;
}
if (typeof window !== 'undefined') {   // phones allow sound only after the first touch of the page
  const unlock = () => { audio(); window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  window.addEventListener('pointerdown', unlock); window.addEventListener('keydown', unlock);
}
function tones(seq, type = 'sine') {
  const ctx = audio(); if (!ctx) return;
  try {
    seq.forEach(([f, t, d]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = type; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + d);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + d + 0.02);
    });
  } catch (e) { /* no audio */ }
}
const SOUND = {
  open: () => tones([[660, 0, 0.18], [880, 0.2, 0.18], [1100, 0.4, 0.3]]),                         // rising: bought
  win: () => tones([[1046, 0, 0.15], [1318, 0.16, 0.15], [1568, 0.32, 0.15], [2093, 0.5, 0.45]]),    // happy chime: profit
  loss: () => tones([[440, 0, 0.35], [330, 0.38, 0.35], [220, 0.76, 0.6]], 'triangle'),              // falling: loss
};
function say(text) {
  try { if (!window.speechSynthesis) return; const u = new SpeechSynthesisUtterance(text); u.lang = 'en-IN'; u.rate = 1.05; window.speechSynthesis.speak(u); } catch (e) { /* ignore */ }
}
async function notify(title, body, tag) {
  try {
    if (!window.Notification || Notification.permission !== 'granted') return;
    const opts = { body, tag, renotify: true, vibrate: [200, 100, 200] };
    const reg = navigator.serviceWorker ? await navigator.serviceWorker.getRegistration() : null;
    if (reg && reg.showNotification) { await reg.showNotification(title, opts); return; }
    new Notification(title, opts);   // eslint-disable-line no-new
  } catch (e) { /* ignore */ }
}
const loadOn = () => { try { return localStorage.getItem('tradeSound') !== 'off'; } catch (e) { return true; } };

export default function TradeSound() {
  const [on, setOn] = useState(loadOn);
  const [toasts, setToasts] = useState([]);
  const seen = useRef(null);
  const onRef = useRef(on);
  onRef.current = on;

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.getMyTradeLog(60);
        if (!alive || r.status !== 'success') return;
        const rows = (r.log || []).filter(x => /^TRADE (OPENED|CLOSED)/.test(x.event || ''));
        const key = x => `${x.date}|${x.ts}|${x.symbol}|${x.event}|${x.detail}`;
        if (seen.current === null) { seen.current = new Set(rows.map(key)); return; }   // first load: no sound for old rows
        const fresh = rows.filter(x => !seen.current.has(key(x))).reverse();               // oldest first
        fresh.forEach(x => seen.current.add(key(x)));
        fresh.forEach((x, i) => setTimeout(() => {
          const opened = /^TRADE OPENED/.test(x.event);
          const m = /P&L Rs\.(-?[\d,]+(?:\.\d+)?)/.exec(x.detail || '');
          const pnl = m ? parseFloat(m[1].replace(/,/g, '')) : 0;
          const reason = ((x.detail || '').split('|')[2] || '').trim();
          const contract = opened ? ((/BUY (\S+)/.exec(x.detail || '') || [])[1] || x.symbol) : x.symbol;
          const title = opened ? `🟢 TRADE TAKEN: ${contract}` : `${pnl >= 0 ? '✅ PROFIT' : '🔴 LOSS'} ₹${Math.round(pnl).toLocaleString('en-IN')}: ${x.symbol} exited`;
          const body = opened ? x.detail : `${reason || 'exit'} · ${x.detail}`;
          const id = `${key(x)}|${Math.random()}`;
          setToasts(t => [...t.slice(-3), { id, title, body, kind: opened ? 'open' : (pnl >= 0 ? 'win' : 'loss') }]);
          setTimeout(() => setToasts(t => t.filter(y => y.id !== id)), 12000);
          if (onRef.current) {
            (opened ? SOUND.open : pnl >= 0 ? SOUND.win : SOUND.loss)();
            say(opened ? `Trade taken, ${x.symbol}` : `${x.symbol} exited, ${pnl >= 0 ? 'profit' : 'loss'} ${Math.abs(Math.round(pnl))} rupees`);
            notify(title, body, `trade-${x.symbol}`);
          }
        }, i * 2500));
      } catch (e) { /* keep last; next poll retries */ }
    };
    poll(); const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const toggle = () => {
    const v = !on; setOn(v);
    try { localStorage.setItem('tradeSound', v ? 'on' : 'off'); } catch (e) { /* ignore */ }
    if (v) { audio(); SOUND.open(); try { if (window.Notification && Notification.permission === 'default') Notification.requestPermission(); } catch (e) { /* ignore */ } }
  };
  const col = { open: '#00d9ff', win: '#00ff88', loss: '#ff4d4d' };
  return (
    <>
      <button onClick={toggle} title="Sound when a trade is taken / exited"
        style={{ position: 'fixed', right: 12, bottom: 124, zIndex: 9001, borderRadius: 20, padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: 700,
                 cursor: 'pointer', background: on ? '#0f3d24' : '#2a2a3a', color: on ? '#00ff88' : '#999', border: `1px solid ${on ? '#00ff88' : '#555'}` }}>
        {on ? '🔊 Trade sounds: ON' : '🔇 Trade sounds: OFF'}
      </button>
      <div style={{ position: 'fixed', top: 64, right: 12, left: 'auto', zIndex: 10002, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 'calc(100vw - 24px)', width: 340 }}>
        {toasts.map(t => (
          <div key={t.id} onClick={() => setToasts(x => x.filter(y => y.id !== t.id))}
            style={{ background: '#11111c', border: `2px solid ${col[t.kind]}`, borderRadius: 10, padding: '0.55rem 0.75rem', boxShadow: '0 6px 20px rgba(0,0,0,0.6)', cursor: 'pointer' }}>
            <div style={{ color: col[t.kind], fontWeight: 800, fontSize: '0.9rem' }}>{t.title}</div>
            <div style={{ color: '#ccc', fontSize: '0.75rem', marginTop: 2, wordBreak: 'break-word' }}>{t.body}</div>
          </div>
        ))}
      </div>
    </>
  );
}
